import React, { useState } from 'react';
import { useOrgConfig, OrgConfig } from "~/providers/OrgConfigProvider";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { CommandIndexCombinedDataQuery, CommandIndexProvidersQuery } from '~/graphql/__generated__/graphql';
import { sortBy, filter } from 'lodash';
import { LocalVisitRequest } from '~/utils/eventsHelper';
import ProviderSelect from '../scheduler/ProviderSelect';
import { SelectProviderData, hasStateLicense } from '~/utils/visitValidationUtils';
import { getAutoAssignment } from '~/utils/providerTeamHelpers';
import { UNASSIGNED_PROVIDER_ID } from '~/utils/visitsHelper';

interface MultiResourceProviderAssignProps {
  visit: LocalVisitRequest;
  startTime: string;
  endTime: string;
  orgConfig: OrgConfig;
  open: boolean;
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  defaultAssignedResourceProviderMap: Record<number, number | null>;
  shifts: CommandIndexCombinedDataQuery["shift"];
  onClose: () => void;
  onCancel: (e: LocalVisitRequest) => void;
  onSave: (e: LocalVisitRequest, assignedResourceProviderMap: Record<number, number>) => void;
}

const MultiResourceProviderAssign: React.FC<MultiResourceProviderAssignProps> = ({
  providers,
  defaultAssignedResourceProviderMap: assignedProvidersMap,
  shifts,
  visit,
  startTime,
  endTime,
  open,
  onClose,
  onCancel,
  onSave
}) => {
  const orgConfig = useOrgConfig();

  const orgConfigRequirements = visit.visitType?.visitResourceRequirements || [];

  const newAssignedProvidersMap: Record<number, number> = orgConfigRequirements.reduce((acc, vrr) => {
    const defaultAssignment = assignedProvidersMap[vrr.id] ?? UNASSIGNED_PROVIDER_ID;
    const assignment = getAutoAssignment(
      vrr,
      Object.entries(assignedProvidersMap).map(([vrrId, resourceId]) => ({ _vrrId: parseInt(vrrId), resourceId: resourceId?.toString() ?? UNASSIGNED_PROVIDER_ID.toString(), offset: 0, duration: 0 })),
      providers,
      shifts,
      startTime,
      endTime,
      undefined,
      true,
      orgConfig
    );
    if (assignment) {
      acc[vrr.id] = parseInt(assignment.updatedData.resourceId, 10);
    } else {
      acc[vrr.id] = defaultAssignment;
    }
    return acc;
  }, {} as Record<number, number>);

  const [assignedResourceProviderMap, setAssignedResourceProviderMap] = useState<Record<number, number>>(newAssignedProvidersMap);

  const sortedProviders = sortBy(filter(providers, p =>
    p.active &&
    shifts.some(shift =>
      shift.provider?.id === p.id &&
      shift.status === "confirmed"
    )
  ), "firstName");

  const selectedCount = Object.entries(assignedResourceProviderMap).filter(([,v]) => v !== UNASSIGNED_PROVIDER_ID && v !== undefined).length;

  const patientState = visit.patient?.addressState ?? null;

  const hasUnlicensedProviders = (() => {
    if (!patientState) return false;

    return orgConfigRequirements.some(req => {
      const providerId = assignedResourceProviderMap[req.id];
      if (!providerId || providerId === UNASSIGNED_PROVIDER_ID) return false;

      const provider = sortedProviders.find(p => p.id === providerId);
      if (!provider) return false;

      const providerType = provider.providerTypeId ? orgConfig.providerTypes[provider.providerTypeId] : null;
      const requiresLicense = (providerType as { requireStateLicense?: boolean })?.requireStateLicense;
      if (!requiresLicense) return false;

      const providerTraits = (provider as { traits?: { trait: string; value: string }[] }).traits;
      return !hasStateLicense(providerTraits, patientState);
    });
  })();

  const isSaveDisabled = selectedCount !== orgConfigRequirements.length || hasUnlicensedProviders;

  // Convert assignment map to SelectProviderData format for ProviderSelect
  const providerData = orgConfigRequirements.map(req => ({
    _vrrId: req.id,
    resourceId: assignedResourceProviderMap[req.id] === UNASSIGNED_PROVIDER_ID || !assignedResourceProviderMap[req.id]
      ? '' 
      : assignedResourceProviderMap[req.id].toString(),
    offset: req.offset || 0,
    duration: req.duration || 0
  }));

  // Handle provider data changes from ProviderSelect
  const handleProviderDataChange = (newProviderData: SelectProviderData[]) => {
    const newAssignmentMap: Record<number, number> = {};
    
    newProviderData.forEach(data => {
      if (data.resourceId && data.resourceId !== '' && data.resourceId !== UNASSIGNED_PROVIDER_ID.toString()) {
        const providerId = parseInt(data.resourceId, 10);
        if (!isNaN(providerId)) {
          // Unassign this provider from other resources first
          Object.entries(newAssignmentMap).forEach(([resId, provId]) => {
            if (provId === providerId && parseInt(resId) !== data._vrrId) {
              newAssignmentMap[parseInt(resId)] = UNASSIGNED_PROVIDER_ID;
            }
          });
          newAssignmentMap[data._vrrId] = providerId;
        }
      } else {
        newAssignmentMap[data._vrrId] = UNASSIGNED_PROVIDER_ID;
      }
    });
    
    setAssignedResourceProviderMap(newAssignmentMap);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
    >
      <DialogTitle>Assign Missing Provider(s)</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Typography variant='subtitle2'>Patient: {visit?.patient?.firstName} {visit?.patient?.lastName}</Typography>
          {visit.patient && (
            <Box sx={{ minWidth: '450px' }}>
              <ProviderSelect
                key={`${visit.patient.id}-${startTime}`}
                loading={false}
                visitResourceRequirements={orgConfigRequirements}
                providersList={sortedProviders}
                prepopulatedProviderList={providerData}
                setProviderData={handleProviderDataChange}
                shifts={shifts}
                startTime={startTime}
                endTime={endTime}
                patient={{
                  id: visit.patient.id,
                  firstName: visit.patient.firstName || '',
                  lastName: visit.patient.lastName || '',
                  careTeamProviders: visit.patient.careTeamProviders,
                  addressState: visit.patient.addressState,
                }}
                offsetFieldsVisibility="never"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { onCancel(visit); onClose(); }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => {
          onSave(visit, assignedResourceProviderMap);
          setAssignedResourceProviderMap({});
          onClose();
          }} disabled={isSaveDisabled}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiResourceProviderAssign;
